/**
 * API de transacciones usando Axios.
 */
import { axiosInstance } from "features/shared/api";
import { walletApi } from "features/wallet/api";
import type { SendConfirmationData } from "ui/transactions/containers/SendConfirmation/SendConfirmation.types";
import type {
    PaginatedTransactionsDto,
    PaginatedResolvedTransactions,
    TransactionDto,
} from "common/models";
import { ResourceInfo } from "common/models";

export interface FindAllTransactionsParams {
    organizationAddress: string;
    page?: number;
    pageSize?: number;
    query?: string;
    address?: string;
    order?: "ASC" | "DESC";
    orderBy?: string;
    startDate?: number;
    endDate?: number;
    mode?: "sent" | "received";
}

export const transactionsApi = {
    findByAddress: (params: FindAllTransactionsParams) =>
        axiosInstance
            .get<PaginatedTransactionsDto>("/api/transactions", { params })
            .then((r) => r.data),

    getTxByHash: (transactionHash: string) =>
        axiosInstance
            .get<TransactionDto>(`/api/transactions/by-hash/${transactionHash}`)
            .then((r) => r.data),

    send: (data: SendConfirmationData) =>
        axiosInstance
            .post<string>("/api/points/send", {
                organizationAddress: data.organizationAddress,
                receiverAddressOrEmail: data.destinationAddress,
                amount: data.amount,
                concept: data.concept,
                reference: data.reference,
                paymentId: data.paymentId,
                otp: data.otp,
            })
            .then((r) => r.data),

    /**
     * Obtiene transacciones y resuelve addresses (sender/receiver) a ResourceInfo.
     */
    findAllResolved: async (params: FindAllTransactionsParams): Promise<PaginatedResolvedTransactions> => {
        const res = await transactionsApi.findByAddress(params);
        return processTransactionsResponse(res);
    },
};

export async function processTransactionsResponse(
    res: PaginatedTransactionsDto
): Promise<PaginatedResolvedTransactions> {
    const addressSet = new Set(
        res.items.reduce(
            (addresses, tx) => [...addresses, tx.senderAddress, tx.receiverAddress],
            [] as string[]
        )
    );
    const { resolutions } = await walletApi.resolveAddresses(Array.from(addressSet));

    return {
        pages: res.pages,
        currentPage: res.currentPage,
        items: res.items.map((tx) => ({
            ...tx,
            senderInfo: ResourceInfo.fromResolvedAddress(resolutions[tx.senderAddress]),
            receiverInfo: ResourceInfo.fromResolvedAddress(resolutions[tx.receiverAddress]),
        })),
        totalItems: res.totalItems,
    };
}
